import React, { useEffect, useMemo, useState } from 'react';
import { TUTORIAL_DASHBOARD } from '../../TutorialsContainer';
import TutorialManager, { Step } from '../../TutorialManager';
import useDeviceDetect from '@src/shared/hooks/use-device-detect';
import { useOnboardingStore } from '@src/shared/store/onboarding/onboarding-store';
import { useDashboardUIStore } from '@src/pages/DeliveryDashboard/store/dashboardUIStore';
import { isMobile } from 'react-device-detect';
import { globalStyles } from 'snacks-design-system';
import { DashboardOnboardingContentStep1 } from './components/StepContents/DashboardOnboardingContentStep1';
import { DashboardOnboardingContentStep2 } from './components/StepContents/DashboardOnboardingContentStep2';
import { DashboardOnboardingContentStep3 } from './components/StepContents/DashboardOnboardingContentStep3';
import { DashboardOnboardingContentStep4 } from './components/StepContents/DashboardOnboardingContentStep4';
import { DashboardOnboardingContentStep5 } from './components/StepContents/DashboardOnboardingContentStep5';
import { DashboardOnboardingContentStep6 } from './components/StepContents/DashboardOnboardingContentStep6';
import { useDashboardOnboardingStepEventHandlers } from './hooks/use-dashboard-onboarding-step-event-handlers';

import DashboardTutorialFinalPopup from './components/DashboardTutorialFinalPopup';
import { DELIVERY_DASHBOARD_BOTTOM_SHEET_ANIMATION_COMPLETE_GLOBAL_EVENT_NAME } from '@src/pages/DeliveryDashboard/components/DeliveryDashboardBottomSheet';

const { xdsRadiusRoundedC } = globalStyles;

export interface renderStepArguments {
  closeTutorial: () => void;
  nextStep: () => void;
  previousStep: () => void;
  stepNumber?: number;
}

interface componentProps {
  onStepChange?: (currentStep: number) => void;
}

const DashboardTutorial = ({ onStepChange }: componentProps) => {
  const { isResolutionMobileInstantCheck } = useDeviceDetect();
  const { setOnboardingActive, activeStep, setActiveStep, setReminderActive } =
    useOnboardingStore();
  const { setBottomSheetForcedSnapHeight, bottomSheetForcedSnapHeight } = useDashboardUIStore();
  const [showFinishPopup, setShowFinishPopup] = useState<boolean>(false);

  const onBeforeStartHandler = () => {
    const style = document.createElement('style');
    style.id = 'temp-tutorial-style';
    style.textContent = `
         [data-rsbs-overlay] { position: absolute !important; }
    `;
    document.head.appendChild(style);
  };
  const onFinishHandler = () => {
    document.head.removeChild(document.getElementById('temp-tutorial-style')!);
  };

  const onAfterEndHandler = () => {
    tutorialAfterAndHandler();
  };

  const stepOnCloseHandler = () => {
    setReminderActive(true);
    setOnboardingActive(false);
    useOnboardingStore.persist.clearStorage();
    setBottomSheetForcedSnapHeight(null);
  };

  const onStepChangeHandler = (currentStep: number) => {
    onStepChange && onStepChange(currentStep);
    if (currentStep === 7) {
      setShowFinishPopup(true);
    }
  };

  const calculatedDelayPromise = () => {
    return new Promise<true>((resolve) => {
      const eventHandler = () => {
        resolve(true);
      };

      window.addEventListener(
        DELIVERY_DASHBOARD_BOTTOM_SHEET_ANIMATION_COMPLETE_GLOBAL_EVENT_NAME,
        eventHandler,
        { once: true }
      );
    });
  };

  useEffect(() => {
    if (activeStep === 7) {
      setShowFinishPopup(true);
    }
  }, []);

  const {
    step3beforeStepInitializedHandler,
    step3beforeStepLeaveHandler,
    step4beforeStepInitializedHandler,
    step5beforeStepInitializedHandler,
    step5beforeStepLeaveHandler,
    step6beforeStepLeaveHandler,
    step6beforeStepInitializedHandler,
    tutorialAfterAndHandler,
  } = useDashboardOnboardingStepEventHandlers();

  const renderStepContent = ({
    stepNumber = 1,
    closeTutorial,
    nextStep,
    previousStep,
  }: renderStepArguments) => {
    const steps = [
      DashboardOnboardingContentStep1,
      DashboardOnboardingContentStep2,
      DashboardOnboardingContentStep3,
      DashboardOnboardingContentStep4,
      DashboardOnboardingContentStep5,
      DashboardOnboardingContentStep6,
    ];
    const StepContent = steps[stepNumber - 1];

    return (
      <StepContent
        nextStep={nextStep}
        previousStep={previousStep}
        closeTutorial={closeTutorial}
        onStepClose={stepOnCloseHandler}
      />
    );
  };

  const steps = useMemo<Array<Step>>(() => {
    return [
      {
        targetElementSelector: '#tutorial-marker-higlighter-1',
        content: ({ closeTutorial, nextStep, previousStep }) =>
          renderStepContent({ stepNumber: 1, closeTutorial, nextStep, previousStep }),
        position: 'bottom',
      },
      {
        targetElementSelector: '#tutorial-marker-higlighter-2',
        content: ({ closeTutorial, nextStep, previousStep }) =>
          renderStepContent({ stepNumber: 2, closeTutorial, nextStep, previousStep }),
        position: 'bottom',
      },
      {
        targetElementSelector: !isResolutionMobileInstantCheck()
          ? '#delivery-dashboard-side-card-toggler'
          : '#delivery-dashboard-bottom-sheet [role="dialog"]',
        content: ({ closeTutorial, nextStep, previousStep }) =>
          renderStepContent({ stepNumber: 3, closeTutorial, nextStep, previousStep }),
        position: isResolutionMobileInstantCheck() ? 'top' : 'right',
        delayBeforeStepInitialize: isResolutionMobileInstantCheck()
          ? () => calculatedDelayPromise()
          : 0,
        beforeStepInitialized: step3beforeStepInitializedHandler,
        beforeStepLeave: step3beforeStepLeaveHandler,
        highlighterCustomCss: !isMobile
          ? {
              borderRadius: xdsRadiusRoundedC,
            }
          : {},
      },
      {
        targetElementSelector: '#manage-delivery-segmented-control-container',
        content: ({ closeTutorial, nextStep, previousStep }) =>
          renderStepContent({ stepNumber: 4, closeTutorial, nextStep, previousStep }),
        beforeStepInitialized: step4beforeStepInitializedHandler,
        delayBeforeStepInitialize:
          !isResolutionMobileInstantCheck() || bottomSheetForcedSnapHeight === 650
            ? 0
            : () => calculatedDelayPromise(),
        position: !isResolutionMobileInstantCheck() ? 'right' : 'bottom',
        highlighterCustomCss: { borderRadius: '23px' },
      },
      {
        targetElementSelector: '#tutorial-marker-higlighter-5',
        content: ({ closeTutorial, nextStep, previousStep }) =>
          renderStepContent({ stepNumber: 5, closeTutorial, nextStep, previousStep }),
        beforeStepInitialized: step5beforeStepInitializedHandler,
        beforeStepLeave: step5beforeStepLeaveHandler,
        delayBeforeStepInitialize:
          !isResolutionMobileInstantCheck() || bottomSheetForcedSnapHeight === 650
            ? 0
            : () => calculatedDelayPromise(),
        highlighterCustomCss: { borderRadius: xdsRadiusRoundedC },
        position: !isResolutionMobileInstantCheck() ? 'right' : 'bottom',
      },
      {
        targetElementSelector: '#dashboard-order-list-filter',
        content: ({ closeTutorial, nextStep, previousStep }) =>
          renderStepContent({ stepNumber: 6, closeTutorial, nextStep, previousStep }),
        beforeStepLeave: step6beforeStepLeaveHandler,
        beforeStepInitialized: step6beforeStepInitializedHandler,
        highlighterCustomCss: {
          borderRadius: xdsRadiusRoundedC,
          marginTop: `-8px`,
          marginLeft: `-8px`,
        },
        position: !isResolutionMobileInstantCheck() ? 'right' : 'bottom',
        delayBeforeStepInitialize:
          !isResolutionMobileInstantCheck() || bottomSheetForcedSnapHeight === 650
            ? 0
            : () => calculatedDelayPromise(),
      },
      {
        targetElementSelector: 'body',
        content: () => <></>,
      },
    ];
  }, [activeStep, bottomSheetForcedSnapHeight]);

  return (
    <>
      <TutorialManager
        onStepChange={onStepChangeHandler}
        predefinedStep={activeStep}
        onBeforeStart={onBeforeStartHandler}
        onAfterEnd={onAfterEndHandler}
        onFinish={onFinishHandler}
        steps={steps}
        tutorialID={TUTORIAL_DASHBOARD}
      />
      {showFinishPopup ? (
        <DashboardTutorialFinalPopup
          onClose={() => {
            setShowFinishPopup(false);
            stepOnCloseHandler();
          }}
          onReplay={() => {
            setBottomSheetForcedSnapHeight(84);
            setShowFinishPopup(false);
            setOnboardingActive(false);
            setActiveStep(1);
            setTimeout(() => {
              setOnboardingActive(true);
            }, 100);
          }}
        />
      ) : (
        <></>
      )}
    </>
  );
};

export default DashboardTutorial;
